import { useEffect, useMemo, useState } from 'react'
import { AlertTriangle, CheckCircle2, Printer, Truck } from 'lucide-react'
import { PARTNER_CATEGORIES, type PartnerCategory } from '@/api/customersApi'
import type { Shipment } from '@/api/shipmentsApi'
import { useCustomersList } from '@/hooks/useCustomers'
import { useCreateShipment, useProductShipments } from '@/hooks/useShipments'
import { useAuth } from '@/providers/AuthProvider'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { categoryOf } from '@/lib/customers'
import { printLabelViaIframe } from '@/lib/printLabel'
import {
  SHIPPING_LABEL_CSS,
  buildShippingLabelHtml,
  formatAddressLines,
  hasShippableAddress,
  isShippable,
  productLocation,
} from '@/lib/shipping'
import type { CreatedProduct } from '@/types/products'
import { SignatureDialog } from '@/components/signature/SignatureDialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useLocationText } from '@/hooks/useStoragePlaces'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

/**
 * Ship Product (z8nrz7eaex) — the popup behind every truck icon: the Product
 * page, the form's product list (reached from the P count or the form's Ship
 * product action), and the board's product list.
 *
 * Category → partner (by CODE) → courier; the partner's address fills in on
 * its own. Partners are the Customer model (z8nrz7earp), each in one category;
 * a courier is a partner in the Courier category. A label can be printed
 * before confirming. Confirm is an e-signature, and on success
 * every product's location becomes the partner's code.
 */
export function ShipProductDialog({
  open,
  onClose,
  initialProducts,
}: {
  open: boolean
  onClose: () => void
  /**
   * The product to ship, picked from a product list's truck icon. One per
   * shipment for now — the API takes a list, so multi-product shipments are a
   * UI change later.
   */
  initialProducts: CreatedProduct[]
}) {
  const { user } = useAuth()
  const locate = useLocationText()
  const [selected, setSelected] = useState<CreatedProduct[]>(initialProducts)
  const [category, setCategory] = useState<PartnerCategory>('Customer')
  const [partnerId, setPartnerId] = useState('')
  const [courierId, setCourierId] = useState('')
  const [trackingNumber, setTrackingNumber] = useState('')
  const [comments, setComments] = useState('')
  const [signing, setSigning] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [shipped, setShipped] = useState<Shipment | null>(null)

  // Each opening is a fresh shipment: start from what the caller picked.
  useEffect(() => {
    if (!open) return
    setSelected(initialProducts)
    setCategory('Customer')
    setPartnerId('')
    setCourierId('')
    setTrackingNumber('')
    setComments('')
    setSigning(false)
    setError(null)
    setShipped(null)
    // initialProducts is a fresh array on every parent render; only the
    // opening itself should reset the form.
  }, [open])

  const partners = useCustomersList({ enabled: open })
  const createShipment = useCreateShipment()

  const inCategory = useMemo(
    () => (partners.data ?? []).filter(p => categoryOf(p) === category),
    [partners.data, category],
  )
  const couriers = useMemo(
    () => (partners.data ?? []).filter(p => categoryOf(p) === 'Courier'),
    [partners.data],
  )
  const partner = partners.data?.find(p => p.id === partnerId) ?? null
  const courier = couriers.find(p => p.id === courierId) ?? null
  const address = partner ? formatAddressLines(partner) : []

  // A product that has ALREADY shipped opens on its shipment instead of the
  // form: its truck icon is how the shipping label gets reprinted.
  const alreadyShipped = selected.length === 1 && !isShippable(selected[0])
  const history = useProductShipments(alreadyShipped ? selected[0].id : null, open && alreadyShipped)
  const existing = history.data?.[0] ?? null
  /** The shipment on screen: the one just made, or the product's earlier one. */
  const view = shipped ?? (alreadyShipped ? existing : null)

  const blocked = selected.filter(p => !isShippable(p))
  const canShip =
    selected.length > 0 && blocked.length === 0 && !!partner && hasShippableAddress(partner) && !shipped

  /**
   * Before Confirm the label shows the partner's live details; after it, the
   * shipment's own SNAPSHOT — a reprint must match the record, even if the
   * partner has been edited since.
   */
  function printLabel(shipment?: Shipment | null) {
    // A shipment's own items are what went in the box; the volume comes from
    // the product rows we hold, when we hold them.
    const products = shipment
      ? shipment.items.map(i => ({
          din: i.din,
          productCode: i.productCode,
          volumeMl: selected.find(p => p.id === i.productId)?.volumeMl ?? null,
        }))
      : selected.map(p => ({ din: p.din, productCode: p.productDescriptionCode, volumeMl: p.volumeMl }))
    const html = shipment
      ? buildShippingLabelHtml({
          shipmentNumber: shipment.number,
          partner: { code: shipment.partnerCode, name: shipment.partnerName },
          destination: shipment.destination,
          courier: shipment.courierCode ? { code: shipment.courierCode, name: shipment.courierName ?? '' } : null,
          trackingNumber: shipment.trackingNumber,
          comments: shipment.comments,
          products,
          date: new Date(shipment.shippedAt),
        })
      : partner
        ? buildShippingLabelHtml({
            partner,
            destination: partner,
            courier,
            trackingNumber: trackingNumber.trim() || null,
            comments: comments.trim() || null,
            products,
            date: new Date(),
          })
        : null
    if (html) printLabelViaIframe(`Shipping label ${shipment?.number ?? ''}`.trim(), html, SHIPPING_LABEL_CSS)
  }

  function confirm(signingToken: string) {
    if (!partner) return
    setError(null)
    createShipment.mutate(
      {
        productIds: selected.map(p => p.id),
        partnerId: partner.id,
        courierId: courier?.id,
        trackingNumber: trackingNumber.trim() || undefined,
        comments: comments.trim() || undefined,
        signingToken,
        signatureMeaning:
          'I am shipping this product',
      },
      {
        onSuccess: shipment => {
          setSigning(false)
          setShipped(shipment)
        },
        onError: err => setError(apiErrorMessage(err, 'Could not ship.')),
      },
    )
  }

  const partnerOptions = inCategory.map(p => ({ value: p.id, label: `${p.code} — ${p.name}` }))
  const courierOptions = couriers.map(p => ({ value: p.id, label: `${p.code} — ${p.name}` }))

  return (
    <>
      <Dialog open={open && !signing} onOpenChange={o => !o && onClose()}>
        <DialogContent className='max-h-[90vh] overflow-y-auto sm:max-w-3xl'>
          <DialogHeader>
            <DialogTitle className='flex items-center gap-2'>
              <Truck className='h-5 w-5' />
              {alreadyShipped ? `Shipping label — ${selected[0].din}` : 'Ship product'}
            </DialogTitle>
            <DialogDescription>
              {alreadyShipped
                ? 'This product has already shipped. Print its shipping label again from the shipment record.'
                : "Choose who you are sending to. Once confirmed and signed, the product's location changes from its storage position to the partner's code."}
            </DialogDescription>
          </DialogHeader>

          {alreadyShipped && !view ? (
            <p className='py-6 text-center text-sm text-muted-foreground'>
              {history.isLoading ? 'Loading the shipment…' : history.isError ? 'Could not load the shipment.' : 'No shipment record found for this product.'}
            </p>
          ) : view ? (
            <div className='space-y-3 rounded-md border border-emerald-300 bg-emerald-50 p-4 text-sm text-emerald-900'>
              <div className='flex items-center gap-2 font-medium'>
                <CheckCircle2 className='h-5 w-5' />
                Shipped on {view.number} · {new Date(view.shippedAt).toLocaleString()}
              </div>
              <div className='grid gap-3 sm:grid-cols-2'>
                <div>
                  <div className='text-xs font-medium uppercase tracking-wide text-emerald-800/80'>Ship to</div>
                  <div className='font-medium'>
                    {view.partnerName} ({view.partnerCode})
                  </div>
                  {formatAddressLines(view.destination).map(line => (
                    <div key={line}>{line}</div>
                  ))}
                </div>
                <div className='space-y-1'>
                  <div>
                    <span className='text-emerald-800/80'>Courier: </span>
                    {view.courierCode ? `${view.courierName} (${view.courierCode})` : '—'}
                  </div>
                  <div>
                    <span className='text-emerald-800/80'>Tracking: </span>
                    {view.trackingNumber || '—'}
                  </div>
                  <div>
                    <span className='text-emerald-800/80'>Products: </span>
                    {view.items.map(i => i.din).join(', ')}
                  </div>
                  <div>
                    <span className='text-emerald-800/80'>Signed by: </span>
                    {view.signerName ?? view.signedBy}
                  </div>
                  {view.comments && (
                    <div>
                      <span className='text-emerald-800/80'>Comments: </span>
                      {view.comments}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className='flex flex-col gap-5'>
              {/* The product being shipped — picked from the product list's truck
                  icon — shown in the same table the product lists use. */}
              <section className='flex flex-col gap-2'>
                <Label>Product to ship</Label>
                <div className='overflow-auto rounded-xl border'>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>DIN</TableHead>
                        <TableHead>Product code</TableHead>
                        <TableHead>Volume (mL)</TableHead>
                        <TableHead>Storage location</TableHead>
                        <TableHead>Created</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selected.map(p => {
                        const loc = productLocation(p)
                        return (
                          <TableRow key={p.id}>
                            <TableCell className='font-medium tabular-nums'>{p.din}</TableCell>
                            <TableCell className='font-mono text-xs'>{p.productDescriptionCode || '—'}</TableCell>
                            <TableCell className='tabular-nums'>
                              {p.volumeMl == null ? <span className='text-muted-foreground'>—</span> : p.volumeMl}
                            </TableCell>
                            <TableCell>
                              {loc.kind === 'stored' ? (
                                <span className='text-xs'>{locate(loc.text)}</span>
                              ) : loc.kind === 'shipped' ? (
                                <span className='font-mono text-xs'>Shipped to {loc.text}</span>
                              ) : (
                                <span className='text-muted-foreground'>Not placed</span>
                              )}
                            </TableCell>
                            <TableCell className='text-muted-foreground'>
                              {new Date(p.createdAt).toLocaleString()}
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </div>
                {blocked.length > 0 && (
                  <p className='flex items-center gap-1.5 text-xs text-destructive'>
                    <AlertTriangle className='h-3.5 w-3.5' />
                    {blocked.map(p => p.din).join(', ')} has already shipped.
                  </p>
                )}
              </section>

              {/* Destination */}
              <section className='grid gap-4 sm:grid-cols-2'>
                <div className='flex flex-col gap-1.5'>
                  <Label htmlFor='ship-category'>Sending to</Label>
                  <Select
                    value={category}
                    onValueChange={v => {
                      setCategory(v as PartnerCategory)
                      setPartnerId('')
                    }}
                  >
                    <SelectTrigger id='ship-category'>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PARTNER_CATEGORIES.map(c => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className='flex flex-col gap-1.5'>
                  <Label>Name [code]</Label>
                  <SearchableSelect
                    value={partnerId}
                    onChange={setPartnerId}
                    options={partnerOptions}
                    placeholder={partners.isLoading ? 'Loading…' : 'Select…'}
                    emptyMessage={
                      partners.isError
                        ? 'Could not load partners.'
                        : 'No partners in this category. Add one under Configure → Partners.'
                    }
                    triggerTitle='Partner'
                  />
                </div>
              </section>

              {partner && (
                <div className='rounded-md border bg-muted/40 px-3 py-2 text-sm'>
                  <div className='mb-1 text-xs font-medium text-muted-foreground'>Ship to</div>
                  <div className='font-medium'>
                    {partner.name} ({partner.code})
                  </div>
                  {address.map(line => (
                    <div key={line}>{line}</div>
                  ))}
                  {!hasShippableAddress(partner) && (
                    <p className='mt-1 flex items-center gap-1.5 text-xs text-destructive'>
                      <AlertTriangle className='h-3.5 w-3.5' />
                      {partner.code} has no complete address. Add street, city and country under
                      Configure → Partners before shipping.
                    </p>
                  )}
                </div>
              )}

              <section className='grid gap-4 sm:grid-cols-2'>
                <div className='flex flex-col gap-1.5'>
                  <Label>Courier</Label>
                  <SearchableSelect
                    value={courierId}
                    onChange={setCourierId}
                    options={courierOptions}
                    placeholder='Select a courier…'
                    emptyMessage='No couriers yet. Add a partner with the Courier category.'
                    triggerTitle='Courier'
                  />
                </div>
                <div className='flex flex-col gap-1.5'>
                  <Label htmlFor='ship-tracking'>Tracking number</Label>
                  <Input
                    id='ship-tracking'
                    value={trackingNumber}
                    onChange={e => setTrackingNumber(e.target.value)}
                    placeholder='Optional'
                    maxLength={100}
                  />
                </div>
              </section>

              <div className='flex flex-col gap-1.5'>
                <Label htmlFor='ship-comments'>Comments</Label>
                <Textarea
                  id='ship-comments'
                  value={comments}
                  onChange={e => setComments(e.target.value)}
                  rows={3}
                  maxLength={1000}
                  className='resize-none'
                  placeholder='Printed on the label and kept on the shipment record'
                />
              </div>
            </div>
          )}

          {error && !signing && <p className='text-sm text-destructive'>{error}</p>}

          <DialogFooter className='gap-2'>
            {alreadyShipped && !view ? (
              <Button onClick={onClose}>Close</Button>
            ) : view ? (
              <>
                <Button variant='outline' onClick={() => printLabel(view)}>
                  <Printer className='mr-2 h-4 w-4' />
                  Print label
                </Button>
                <Button onClick={onClose}>Done</Button>
              </>
            ) : (
              <>
                <Button variant='outline' onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  variant='outline'
                  onClick={() => printLabel()}
                  disabled={!partner || selected.length === 0}
                >
                  <Printer className='mr-2 h-4 w-4' />
                  Print label
                </Button>
                <Button
                  onClick={() => {
                    setError(null)
                    setSigning(true)
                  }}
                  disabled={!canShip}
                >
                  <Truck className='mr-2 h-4 w-4' />
                  Confirm shipment
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SignatureDialog
        open={open && signing}
        onOpenChange={o => {
          if (!o) setSigning(false)
        }}
        title={`Ship ${selected[0]?.din ?? 'product'} to ${partner?.code ?? ''}?`}
        description={
          <>
            The product leaves its storage position and its location becomes <strong>{partner?.code}</strong>{' '}
            {partner?.name}. This is recorded in the audit trail with your signature.
          </>
        }
        confirmLabel='Ship & Sign'
        signerName={user?.fullName || user?.email}
        busy={createShipment.isPending}
        error={error}
        onConfirm={confirm}
      />
    </>
  )
}
